import React, { ReactElement } from 'react';
import isEqual from 'lodash/isEqual';
import { i18n } from 'config/locales';
import { FieldModel, LocationModel } from 'forms/shared/types';
import ValidationElement from '../ValidationElement';
import Text from '../Text';
import Branch from '../Branch';
import NotApplicable from '../NotApplicable';
import Field from '../Field';
import Textarea from '../Textarea';
import ZipCode from '../ZipCode';
import { setAttribute } from '../shared/setAttribute';

export default class MilitaryInstallation extends ValidationElement {
  constructor(props) {
    super(props);

    this.state = {
      value: props.value,
    };

    this.apoFpoDpoPostcode = React.createRef();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(nextProps.value, this.props.value)) {
      this.setState({
        value: nextProps.value,
      });
    }
  }

  update = (queue: Partial<LocationModel>) => {
    this.props.onUpdate({
      isMilitaryDiplomaticAddress: this.props.isMilitaryDiplomaticAddress,
      militaryDiplomaticInstallationName: this.props.militaryDiplomaticInstallationName,
      militaryInstallationNameNotApplicable: this.props.militaryInstallationNameNotApplicable,
      militaryDiplomaticInstallationNameExplanation: this.props.militaryDiplomaticInstallationNameExplanation,
      apoFpoDpoPostcode: this.props.apoFpoDpoPostcode,
      apoFpoNotApplicable: this.props.apoFpoNotApplicable,
      ...queue,
    });
  };

  updateIsMilitaryDiplomaticAddress = (values: LocationModel['isMilitaryDiplomaticAddress']) => {
    const additionalUpdates = values?.value && values.value !== 'Yes' ? {
      apoFpoDpoPostcode: '',
      militaryDiplomaticInstallationName: '',
      militaryInstallationNameNotApplicable: { applicable: true },
      militaryDiplomaticInstallationNameExplanation: '',
    } : {};

    this.update({
      isMilitaryDiplomaticAddress: values,
      ...additionalUpdates,
    });
  };

  updateMilitaryDiplomaticInstallationName = (values: Partial<FieldModel>) => {
    this.update({
      militaryDiplomaticInstallationName: values?.value || '',
    });
  };

  updateMilitaryInstallationNameNotApplicable = (values: Partial<LocationModel['militaryInstallationNameNotApplicable']>) => {
    const saveValue = values?.applicable === undefined ? true : !!values?.applicable;

    const queue: Partial<LocationModel> = {
      militaryInstallationNameNotApplicable: {
        applicable: saveValue,
      }
    };

    if (!saveValue) {
      queue.militaryDiplomaticInstallationName = '';
    }

    this.update(queue);
  };

  updateMilitaryDiplomaticInstallationNameExplanation = (values: Partial<FieldModel>) => {
    this.update({
      militaryDiplomaticInstallationNameExplanation: values?.value || '',
    });
  };

  updateApoFpoDpoPostcode = (values: Partial<FieldModel>) => {
    this.update({
      apoFpoDpoPostcode: values?.value || '',
    });
  };

  updateApoFpoNotApplicable = (values: Partial<LocationModel['apoFpoNotApplicable']>) => {
    const saveValue = values?.applicable !== false;
    this.update({
      apoFpoNotApplicable: { applicable: saveValue },
      apoFpoDpoPostcode: values?.applicable === false ? '' : this.props.apoFpoDpoPostcode,
    });
  };

  getInstallationNameComponent = () => (
    <Field
      title={this.props.nameTitle}
      titleSize="label"
      adjustFor="textarea"
      attribute={setAttribute(this.props.attribute, 'militaryDiplomaticInstallationName')}
    >
      <Text
        name="MilitaryDiplomaticInstallationName"
        attribute={setAttribute(this.props.attribute, 'militaryDiplomaticInstallationName')}
        labelSize="h4"
        value={this.props.militaryDiplomaticInstallationName}
        disabled={this.props.disabled || this.props.militaryInstallationNameNotApplicable?.applicable === false}
        onUpdate={this.updateMilitaryDiplomaticInstallationName}
        onError={this.props.onError}
        required={this.props.required}
        scrollIntoView={this.props.scrollIntoView}
      />
    </Field>
  );

  getInstallationNameWithUnknownComponent = () => (
    <Field
      title={this.props.nameTitle}
      titleSize="label"
      adjustFor="textarea"
      attribute={setAttribute(this.props.attribute, 'militaryDiplomaticInstallationName')}
    >
      <NotApplicable
        attribute={setAttribute(this.props.attribute, 'militaryInstallationNameNotApplicable')}
        disabled={this.props.disabled}
        exactAttribute
        {...this.props.militaryInstallationNameNotApplicable}
        label={i18n.t('address.pvq.installation.nameNA.label')}
        or={i18n.t('address.pvq.installation.nameNA.or')}
        onUpdate={this.updateMilitaryInstallationNameNotApplicable}
        includeNAConfirmation={!!(this.props.militaryDiplomaticInstallationName)}
      >
        <Text
          name="MilitaryDiplomaticInstallationName"
          attribute={setAttribute(this.props.attribute, 'militaryDiplomaticInstallationName')}
          labelSize="h4"
          value={this.props.militaryDiplomaticInstallationName}
          disabled={this.props.disabled || this.props.militaryInstallationNameNotApplicable?.applicable === false}
          onUpdate={this.updateMilitaryDiplomaticInstallationName}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
      </NotApplicable>
    </Field>
  );

  getApoFpoWithUnknownComponent = () => (
    <Field
      attribute={setAttribute(this.props.attribute, 'apoFpoDpoPostcode')}
    >
      <NotApplicable
        attribute={setAttribute(this.props.attribute, 'apoFpoNotApplicable')}
        disabled={this.props.disabled}
        exactAttribute
        {...this.props.apoFpoNotApplicable}
        label={i18n.t('address.pvq.installation.nameNA.label')}
        or={i18n.t('address.pvq.installation.nameNA.or')}
        onUpdate={this.updateApoFpoNotApplicable}
        includeNAConfirmation={!!(this.props.apoFpoDpoPostcode)}
      >
        <ZipCode
          attribute={setAttribute(this.props.attribute, 'apoFpoDpoPostcode')}
          name="apoFpoDpoPostcode"
          className="zipcode required"
          label={i18n.t('military.pvq.disciplinary.label.apofpodpozipcode')}
          value={this.props.apoFpoDpoPostcode}
          disabled={this.props.disabled || this.props.apoFpoNotApplicable?.applicable === false}
          onUpdate={this.updateApoFpoDpoPostcode}
          onError={this.props.onError}
          required={this.props.required}
        />
      </NotApplicable>
    </Field>
  );

  render() {
    return (
      <div>
        <Branch
          {...this.props.isMilitaryDiplomaticAddress}
          name="isMilitaryDiplomaticAddress"
          attribute={setAttribute(this.props.attribute, 'isMilitaryDiplomaticAddress')}
          disabled={this.props.disabled}
          dnkLabel={this.props.branchDnkLabel}
          label={this.props.branchLabel}
          labelSize="h4"
          onUpdate={this.updateIsMilitaryDiplomaticAddress}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          showDNK={this.props.showIsInstallationUnknown}
        />
        {this.props.isMilitaryDiplomaticAddress?.value === 'Yes' && (
          <div>
            {this.props.isForeignInstallation === true && (
              <div>
                {this.props.hideInstallationName !== true && (
                  <>
                    {
                      this.props.hideInstallationNameUnknownWhenForeign ? (<>{ this.getInstallationNameComponent() }</>)
                        : (
                          <NotApplicable
                            attribute={setAttribute(this.props.attribute, 'militaryInstallationNameNotApplicable')}
                            disabled={this.props.disabled}
                            exactAttribute
                            {...this.props.militaryInstallationNameNotApplicable}
                            label={i18n.t('address.pvq.installation.nameNA.label')}
                            or={i18n.t('address.pvq.installation.nameNA.or')}
                            onUpdate={this.updateMilitaryInstallationNameNotApplicable}
                            includeNAConfirmation={!!(this.props.militaryDiplomaticInstallationName)}
                          >
                            {this.getInstallationNameComponent()}
                          </NotApplicable>
                        )
                    }
                    {this.props.hideInstallationNameUnknownExplanation !== true
                      && this.props.militaryInstallationNameNotApplicable?.applicable === false && (
                      <Field
                        title={i18n.t('address.pvq.installation.nameExplanation.label')}
                        titleSize="label"
                        adjustFor="textarea"
                        attribute={setAttribute(this.props.attribute, 'militaryDiplomaticInstallationNameExplanation')}
                      >
                        <Textarea
                          attribute={setAttribute(this.props.attribute, 'militaryDiplomaticInstallationNameExplanation')}
                          disabled={this.props.disabled}
                          exactAttribute
                          name="MilitaryDiplomaticInstallationNameExplanation"
                          value={this.props.militaryDiplomaticInstallationNameExplanation}
                          onUpdate={this.updateMilitaryDiplomaticInstallationNameExplanation}
                          onError={this.props.onError}
                        />
                      </Field>
                    )}
                  </>
                )}
              </div>
            )}
            {this.props.isForeignInstallation !== true && this.props.showInstallationNameUnknown === true && (
              this.getInstallationNameWithUnknownComponent()
            )}
            {this.props.isForeignInstallation !== true && this.props.showInstallationNameUnknown !== true && (
              this.getInstallationNameComponent()
            )}
            {this.props.isForeignInstallation !== true && this.props.showApoFpoDpoPostcode && (
              this.renderApoFpoDpoPostcode()
            )}
            {this.props.isForeignInstallation === true && this.props.showApoFpoUnknown === true && (
              this.getApoFpoWithUnknownComponent()
            )}
            {this.props.isForeignInstallation === true && this.props.showApoFpoDpoPostcodeWhenForeign && (
              this.renderApoFpoDpoPostcode()
            )}
          </div>
        )}
      </div>
    );
  }

  private renderApoFpoDpoPostcode(): ReactElement {
    return (
      <Field
        attribute={setAttribute(this.props.attribute, 'apoFpoDpoPostcode')}
      >
        <ZipCode
          attribute={setAttribute(this.props.attribute, 'apoFpoDpoPostcode')}
          className="zipcode required"
          disabled={this.props.disabled}
          label={this.props.apoFpoDpoPostcodeLabel}
          name="apoFpoDpoPostcode"
          onError={this.props.onError}
          onUpdate={this.updateApoFpoDpoPostcode}
          required={this.props.required}
          showDisabledText={this.props.showDisabledText}
          value={this.props.apoFpoDpoPostcode}
        />
      </Field>
    );
  }
}

MilitaryInstallation.defaultProps = {
  apoFpoDpoPostcodeLabel: i18n.t('address.pvq.installation.zipcode.label'),
  branchDnkLabel: i18n.t('branch.label.dnk'),
  branchLabel: i18n.t('address.pvq.installation.branch.label'),
  disabled: false,
  hideInstallationName: false,
  hideInstallationNameUnknownExplanation: false,
  nameTitle: i18n.t('address.pvq.installation.name.title'),
  showInstallationNameUnknown: false,
  showApoFpoUnknown: false,
  showApoFpoDpoPostcodeWhenForeign: false,
  hideInstallationNameUnknownWhenForeign: false,
  value: '',
  label: '',
  optional: false,
  isForeignInstallation: '',
  apoFpoDpoPostcode: '',
  isMilitaryDiplomaticAddress: '',
  militaryDiplomaticInstallationName: '',
  militaryInstallationNameNotApplicable: { applicable: true },
  militaryDiplomaticInstallationNameExplanation: '',
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  showDisabledText: false,
  showIsInstallationUnknown: false,
  showApoFpoDpoPostcode: false,
  apoFpoNotApplicable: { applicable: true },
};

MilitaryInstallation.errors = [];
